// OMNI Design System Color Tokens for Chakra UI v3
// Mapped from omni_standard_css.css

export const omniColors = {
  // Pure colors
  white: { value: '#ffffff' },
  black: { value: '#000000' },

  // Primary brand colors
  brand: {
    50: { value: '#e6f7fd' }, // Very light blue
    100: { value: '#b3e8f8' }, // Light blue
    200: { value: '#80d9f3' }, // Lighter electric blue
    300: { value: '#4dcaee' }, // Light electric blue
    400: { value: '#1abce9' }, // Medium electric blue
    500: { value: '#00a1d2' }, // Primary electric blue (--rgb-primary)
    600: { value: '#0091be' }, // Darker electric blue
    700: { value: '#0081aa' }, // Even darker
    800: { value: '#007196' }, // Dark electric blue
    900: { value: '#006182' }, // Darkest electric blue
  },

  // Secondary brand colors
  secondary: {
    50: { value: '#e6f9fd' }, // Very light secondary
    100: { value: '#b3f0fa' }, // Light secondary
    200: { value: '#80e7f7' }, // Lighter secondary
    300: { value: '#4ddef4' }, // Light secondary
    400: { value: '#1ad5f1' }, // Medium secondary
    500: { value: '#03bbe3' }, // Secondary blue (--rgb-secondary)
    600: { value: '#03a8cc' }, // Darker secondary
    700: { value: '#0295b5' }, // Even darker
    800: { value: '#02829e' }, // Dark secondary
    900: { value: '#026f87' }, // Darkest secondary
  },

  // Navigation colors
  navbar: {
    50: { value: '#e8eaf0' }, // Light nav
    100: { value: '#c5ccd9' }, // Lighter nav
    200: { value: '#a2aec2' }, // Medium light nav
    300: { value: '#7f90ab' }, // Medium nav
    400: { value: '#5c7294' }, // Medium dark nav
    500: { value: '#2b3952' }, // Nav one (--rgb-nav-one)
    600: { value: '#26334a' }, // Darker nav
    700: { value: '#212d42' }, // Even darker
    800: { value: '#1c273a' }, // Very dark nav
    900: { value: '#142033' }, // Nav two (--rgb-nav-two)
  },

  // Accent colors
  accent: {
    // Accent One - Light Blue
    blue: {
      50: { value: '#e9f8ff' },
      100: { value: '#c7ecfe' },
      200: { value: '#a5e0fd' },
      300: { value: '#83d4fc' },
      400: { value: '#65cbfc' },
      500: { value: '#4dc5fc' }, // --rgb-accent-one
      600: { value: '#2bb1f4' },
      700: { value: '#219ce3' },
      800: { value: '#1a87d1' },
      900: { value: '#1572bf' },
    },

    // Accent Two - Mint Green
    mint: {
      50: { value: '#e8f9f5' },
      100: { value: '#c4f0e5' },
      200: { value: '#a0e7d5' },
      300: { value: '#7cdec5' },
      400: { value: '#58d5b5' },
      500: { value: '#82d1ab' }, // --rgb-accent-two
      600: { value: '#2cc4ad' }, // Used in success
      700: { value: '#25b09a' },
      800: { value: '#1e9c87' },
      900: { value: '#178874' },
    },

    // Accent Three - Orange
    orange: {
      50: { value: '#fff8e6' },
      100: { value: '#ffecb3' },
      200: { value: '#ffe080' },
      300: { value: '#ffd44d' },
      400: { value: '#ffc81a' },
      500: { value: '#ffa600' }, // --rgb-accent-three
      600: { value: '#e69500' },
      700: { value: '#cc8400' },
      800: { value: '#b37300' },
      900: { value: '#996200' },
    },

    // Accent Four - Pink
    pink: {
      50: { value: '#faeaf1' },
      100: { value: '#f2c7d8' },
      200: { value: '#eaa4bf' },
      300: { value: '#e281a6' },
      400: { value: '#da5e8d' },
      500: { value: '#d45087' }, // --rgb-accent-four
      600: { value: '#c8407a' },
      700: { value: '#bc306d' },
      800: { value: '#b02060' },
      900: { value: '#a41053' },
    },
  },

  // Core neutral colors
  gray: {
    50: { value: '#ffffff' }, // --rgb-core-light
    100: { value: '#f1f5fa' }, // --rgb-core-medium-two
    200: { value: '#edf0f5' }, // --rgb-border
    300: { value: '#e0e4e9' }, // Interpolated
    400: { value: '#c8cdd3' }, // Interpolated
    500: { value: '#a2a9ad' }, // --rgb-core-medium
    600: { value: '#8a9196' }, // Interpolated
    700: { value: '#6b7075' }, // Interpolated
    800: { value: '#4c5054' }, // Interpolated
    900: { value: '#3b3e3f' }, // --rgb-core-dark
  },

  // Status colors (semantic)
  blue: {
    50: { value: '#e6f9fd' },
    100: { value: '#b3f0fa' },
    200: { value: '#80e7f7' },
    300: { value: '#4ddef4' },
    400: { value: '#1ad5f1' },
    500: { value: '#03bbe3' }, // --color-info
    600: { value: '#03a8cc' },
    700: { value: '#0295b5' },
    800: { value: '#02829e' },
    900: { value: '#026f87' },
  },

  green: {
    50: { value: '#e8f9f5' },
    100: { value: '#c4f0e5' },
    200: { value: '#a0e7d5' },
    300: { value: '#7cdec5' },
    400: { value: '#58d5b5' },
    500: { value: '#2cc4ad' }, // --color-success
    600: { value: '#25b09a' },
    700: { value: '#1e9c87' },
    800: { value: '#178874' },
    900: { value: '#107461' },
  },

  red: {
    50: { value: '#fce8ed' },
    100: { value: '#f7bfcc' },
    200: { value: '#f296ab' },
    300: { value: '#ed6d8a' },
    400: { value: '#e84469' },
    500: { value: '#df5678' }, // --color-error
    600: { value: '#c8405e' },
    700: { value: '#b12a44' },
    800: { value: '#9a142a' },
    900: { value: '#830010' },
  },

  yellow: {
    50: { value: '#fffbf0' },
    100: { value: '#fff4d6' },
    200: { value: '#ffedbc' },
    300: { value: '#ffe6a2' },
    400: { value: '#ffdf88' },
    500: { value: '#fed882' }, // --color-alert
    600: { value: '#f8d991' }, // --color-alert-two
    700: { value: '#e6c374' },
    800: { value: '#d4ad57' },
    900: { value: '#c2973a' },
  },

  // Visualization colors (for charts/graphs)
  visualization: {
    deepBlue: { value: '#003f5c' },
    persianBlue: { value: '#212ad2' },
    midBlue: { value: '#2f4b7c' },
    royalBlue: { value: '#0c67e6' },
    violet: { value: '#665191' },
    plum: { value: '#a05195' },
    rose: { value: '#d45087' },
    deepCoral: { value: '#f95d6a' },
    tangerine: { value: '#ff7c43' },
    lightOrange: { value: '#ffa600' },
    lightBlue: { value: '#4dc5fc' },
    periwinkle: { value: '#7ea5ff' },
    lilac: { value: '#c596ff' },
    mediumPurple: { value: '#9e78ea' },
    bubbleGum: { value: '#fb83e2' },
    pink: { value: '#ff75b5' },
    seaBuckthorn: { value: '#fbb32d' },
    lightCoral: { value: '#ff7781' },
    lightTangerine: { value: '#fe975e' },
    sandyBrown: { value: '#f19b50' },
    seaFoam: { value: '#8ce6bb' },
    pastelGreen: { value: '#66ef95' },
    neutral: { value: '#c5d3de' },
  },
};

// Legacy brand colors (keeping for backward compatibility)
export const legacyBrandColors = {
  indigo: {
    50: { value: '#cfdbe1' },
    100: { value: '#a0b6c3' },
    200: { value: '#88a4b5' },
    300: { value: '#588097' },
    400: { value: '#416d88' },
    500: { value: '#11496a' },
    600: { value: '#0f425f' },
    700: { value: '#0e3a55' },
    800: { value: '#0c334a' },
    900: { value: '#092535' },
  },
  omd_red: {
    50: { value: '#fdeae9' },
    100: { value: '#fac1bd' },
    200: { value: '#f79892' },
    300: { value: '#f36f66' },
    400: { value: '#f0463a' },
    500: { value: '#ee3124' },
    600: { value: '#d62c20' },
    700: { value: '#a72219' },
    800: { value: '#5f140e' },
    900: { value: '#180504' },
  },
  phd_purple: {
    50: { value: '#ebe8ee' },
    100: { value: '#c2bbcd' },
    200: { value: '#9a8eac' },
    300: { value: '#72618a' },
    400: { value: '#493469' },
    500: { value: '#351d58' },
    600: { value: '#301a4f' },
    700: { value: '#25143e' },
    800: { value: '#1b0f2c' },
    900: { value: '#0b0612' },
  },
  phd_pink: {
    50: { value: '#fbe6ee' },
    100: { value: '#f3b3cc' },
    200: { value: '#ef99bb' },
    300: { value: '#e6679a' },
    400: { value: '#de3478' },
    500: { value: '#d60156' },
    600: { value: '#c1014d' },
    700: { value: '#ab0145' },
    800: { value: '#800134' },
    900: { value: '#560022' },
  },
  hs_pink: {
    50: { value: '#ffe6f5' },
    100: { value: '#ffcceb' },
    200: { value: '#ffb3e0' },
    300: { value: '#ff80cc' },
    400: { value: '#ff33ad' },
    500: { value: '#ff0099' },
    600: { value: '#e6008a' },
    700: { value: '#b3006b' },
    800: { value: '#80004d' },
    900: { value: '#66003d' },
  },
};
